import i18nFactory from '@yandex-int/i18n';
import { toPath } from 'lodash';

import { getEditedInternalBannerImage } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import {
    selectEditedInternalBannerTemplateResources,
    selectInternalBannerResources
} from '../../selectors/internalBannerEdit/internalBannerEdit';
import { BannerId } from '../../typings/banners';
import { ProtocolValue } from '../../typings/bannersEditState';
import { ErrorsForValidationErrorsData, ValidationErrorData, ValidationResultItem } from '../../typings/common';
import { AdvType, CampaignId } from '../../typings/domain';
import {
    adsInternalRowFragmentForEdit_images,
    GdInternalTemplateInfoRequestEntryInput,
    GdResourceType
} from '../../typings/graphql-grid';
import { GroupId } from '../../typings/groups';
import {
    InternalResourceId,
    InternalTemplateId,
    InternalTemplateResourceImages,
    InternalTemplateResources,
    InternalTemplateVars
} from '../../typings/internalBanners';
import { EditedInternalBanner, EditInternalBannersState } from '../../typings/internalBannersEditState';
import { ImageEditorsState, State } from '../../typings/state';
import { internalBannerFieldErrorMapper } from '../BannerEditErrors/BannerEditErrors';
import {
    internalBannersErrorsMapper,
    internalImageUploadErrorMapper
} from '../BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { InternalBannerValidator } from '../BannerEditValidation/InternalBannerValidator';
import { getBannerEditPageItemEditorId, getEmptyBannerImage } from '../BannerHelpers/BannerHelpers';
import {
    buildErrorTree,
    convertServerPathToClient,
    mapTextToErrors,
    mapTextToValidationErrorData
} from '../ErrorsMapper/ErrorsMapper';
import { getImageEditorIdByBannerId } from '../ImageHelpers/ImageHelpers';

import * as keyset from './InternalBannerHelpers.i18n';

const i18n = i18nFactory(keyset);

export function generateNewTemplateVars(
    state: EditInternalBannersState,
    campaignId: CampaignId,
    templateId: InternalTemplateId,
    templateVars?: InternalTemplateVars[]
): InternalTemplateVars[] {
    const list = new Map<string, InternalTemplateVars>();
    const resources = selectInternalBannerResources(state, campaignId, templateId);

    if (templateVars) {
        templateVars.forEach(value => list.set(value.resourceId, value));
    }

    if (resources) {
        resources.forEach(value => {
            if (!list.has(value.id)) {
                list.set(value.id, {
                    value: null,
                    templateId: value.templateId,
                    resourceId: value.id
                });
            }
        });
    }

    return Array.from(list.values());
}

export const getInternalBannerDescriptionEditorValidationResult = (
    state: State,
    bannerId: BannerId
): ValidationErrorData[] => {
    const { editPageItemNameEditor: editors } = state;
    const editorId = getBannerEditPageItemEditorId(bannerId);
    const editorData = selectEditPageItemNameEditorValue(editors, editorId);

    return getInternalBannerDescriptionValidationResult(state, bannerId, editorData.value);
};

export const getInternalBannerDescriptionValidationResult = (
    state: State,
    bannerId: BannerId,
    description: string
): ValidationErrorData[] => {
    const {
        editInternalBanners: { banners }
    } = state;

    const banner = banners[bannerId];
    const errors = InternalBannerValidator.validateDescription({
        ...banner,
        description
    });

    const clientValidationItems = convertServerPathToClient(errors, internalBannerFieldErrorMapper);

    return mapTextToValidationErrorData(clientValidationItems, internalBannersErrorsMapper);
};

export const getInternalBannersImageEditorsErrors = (
    errors: ValidationResultItem[],
    ids: BannerId[],
    state: EditInternalBannersState
): ({
    bannerId: BannerId;
    resourceId: InternalResourceId;
    errors: ValidationErrorData[];
})[] => {
    const clientValidationItems = convertServerPathToClient(errors, internalBannerFieldErrorMapper);
    const errorsClientWithText = mapTextToErrors(clientValidationItems, internalImageUploadErrorMapper);
    const { banners: bannersErrorTree } = buildErrorTree<{
        banners?: Record<
            string,
            {
                templateVars: Record<string, Required<ErrorsForValidationErrorsData>>;
            }
        >;
    }>(errorsClientWithText);

    if (!bannersErrorTree) {
        return [];
    }

    return Object.keys(bannersErrorTree).reduce<
        ({
            bannerId: BannerId;
            resourceId: InternalResourceId;
            errors: ValidationErrorData[];
        })[]
    >((editorsErrors, key) => {
        const bannerId = ids[key];
        const errorsTree = bannersErrorTree[key] && bannersErrorTree[key].templateVars;
        const resources = selectEditedInternalBannerTemplateResources(state, bannerId);

        if (resources && errorsTree) {
            Object.keys(errorsTree).forEach(index => {
                const resource = resources[index];

                if (resource && resource.type === GdResourceType.IMAGE && errorsTree[index].errors) {
                    editorsErrors.push({
                        bannerId,
                        resourceId: resource.id,
                        errors: errorsTree[index].errors
                    });
                }
            });
        }

        return editorsErrors;
    }, []);
};

export const getInternalBannersImageEditorsState = (
    entries: GdInternalTemplateInfoRequestEntryInput[],
    images: InternalTemplateResourceImages,
    templateResources: InternalTemplateResources
): ImageEditorsState => {
    return entries.reduce<ImageEditorsState>((state, { campaignId, templateIds }) => {
        templateIds.forEach(templateId => {
            templateResources[campaignId][templateId].resources.forEach(resource => {
                if (resource.type === GdResourceType.IMAGE) {
                    Object.keys(images).forEach((bannerId: BannerId) => {
                        const id = getImageEditorIdByBannerId(bannerId, resource.id);
                        const imges: adsInternalRowFragmentForEdit_images | undefined = images[bannerId][resource.id];

                        state[id] = {
                            imageGallery: {
                                images: undefined,
                                isImageGalleryOpen: false,
                                selectedImageHashFromGallery: []
                            },
                            image: imges ? getEditedInternalBannerImage(imges) : getEmptyBannerImage(),
                            imageUploadingLink: '',
                            isCropOpen: false,
                            isImageCanvasOpen: false
                        };
                    });
                }
            });
        });

        return state;
    }, {});
};

export const splitInternalBannersOnNewAndEdited = (
    state: State,
    banners: Record<BannerId, EditedInternalBanner>
): {
    editedBanners: Record<string, EditedInternalBanner>;
    newBanners: Record<string, EditedInternalBanner>;
    campaignsIds: CampaignId[];
    groupsIds: GroupId[];
} => {
    const { editPageItemNameEditor: editors } = state;
    const editedBanners = {};
    const newBanners = {};
    const campaignsIds = new Set<CampaignId>();
    const groupsIds = new Set<GroupId>();

    Object.keys(banners).forEach(bannerId => {
        const banner = { ...banners[bannerId] };

        campaignsIds.add(banner.campaign.id);
        groupsIds.add(banner.group.id);

        if (banner.isSaved) {
            return;
        }

        const editorData = selectEditPageItemNameEditorValue(editors, getBannerEditPageItemEditorId(bannerId));

        if (editorData.isVisible) {
            banner.description = editorData.value;
        }

        if (banners[bannerId].isNew) {
            newBanners[bannerId] = banner;
        } else if (!banners[bannerId].markedForDeletion) {
            editedBanners[bannerId] = banner;
        }
    });

    return {
        editedBanners,
        newBanners,
        campaignsIds: [...campaignsIds],
        groupsIds: [...groupsIds]
    };
};

export const isInternalBannerCheck = (bannerType: AdvType): boolean => [AdvType.INTERNAL].includes(bannerType);

export const splitUrlWithSchema = (value: string): { url: string; schema: ProtocolValue | undefined } => {
    let schema: ProtocolValue | undefined;

    if (value.startsWith(ProtocolValue.http)) {
        schema = ProtocolValue.http;
    }

    if (value.startsWith(ProtocolValue.https)) {
        schema = ProtocolValue.https;
    }

    return {
        schema,
        url: schema ? value.replace(schema, '') : value
    };
};

export const getResourceFieldErrorText = (
    index: number,
    templateVarsErrors: ValidationErrorData[]
): ValidationErrorData[] | undefined => {
    const errors: ValidationErrorData[] = [];

    templateVarsErrors.forEach(error => {
        const errPath = toPath(error.path);

        if (errPath[errPath.length - 1] === index.toString()) {
            errors.push(error);
        }
    });

    return errors;
};

type EditedInternalBannerRequired = 'id' | 'isNew' | 'campaign' | 'group';
type EditedInternalBannerData = Partial<Exclude<EditedInternalBanner, EditedInternalBannerRequired>> &
    Required<Pick<EditedInternalBanner, EditedInternalBannerRequired>>;

export const createInternalEditedBanner = (bannerData: EditedInternalBannerData): EditedInternalBanner => {
    return {
        bannerType: AdvType.INTERNAL,
        isMobile: false,
        isSaved: false,
        isEditAllow: true,
        markedForDeletion: false,
        canBeDeleted: true,
        readOnly: false,
        errors: undefined,
        status: {
            isPreviousVersionShown: false,
            isRejectedOnModeration: false
        },
        description: i18n('new-banner'),
        templateVars: [],
        templateId: '0',

        ...bannerData
    };
};

export const mapInternalBannerErrors = (code: string): string => {
    switch (code) {
        case 'wrong-place-id':
            return i18n('wrong-place-id');
        default:
            return i18n('default-error');
    }
};
